import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../AuthContext";
import { listUsers, createUser, updateUserRole, resetUserPassword, deleteUser, getMyProfile } from "../usersApi";

const inputStyle = {
  width: "100%", padding: "9px 11px", borderRadius: 8, border: "1px solid #cbd5e1",
  fontSize: 13, outline: "none", boxSizing: "border-box",
};
const btnBase = {
  padding: "7px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600,
  cursor: "pointer", border: "1px solid transparent",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [myProfile, setMyProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", role: "admin" });

  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [profile, { users: list }] = await Promise.all([getMyProfile(), listUsers()]);
      setMyProfile(profile);
      setUsers(list);
    } catch (err) {
      setError(err.message || "Gagal memuat daftar user.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const isAdmin = myProfile?.role === "admin";

  const handleCreate = async (e) => {
    e.preventDefault();
    setActionError("");
    setActionBusy(true);
    try {
      await createUser(form.email.trim(), form.password, form.role, form.full_name.trim());
      setForm({ email: "", password: "", full_name: "", role: "admin" });
      setShowForm(false);
      await refresh();
    } catch (err) {
      setActionError(err.message || "Gagal membuat user.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    setActionError("");
    setActionBusy(true);
    try {
      await updateUserRole(userId, role);
      await refresh();
    } catch (err) {
      setActionError(err.message || "Gagal mengubah role.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Hapus akun "${u.email}"? Tindakan ini tidak bisa dibatalkan.`)) return;
    setActionError("");
    setActionBusy(true);
    try {
      await deleteUser(u.id);
      await refresh();
    } catch (err) {
      setActionError(err.message || "Gagal menghapus user.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setActionError("");
    setActionBusy(true);
    try {
      await resetUserPassword(resetTarget.id, resetPassword);
      setResetTarget(null);
      setResetPassword("");
    } catch (err) {
      setActionError(err.message || "Gagal reset password.");
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 28, fontFamily: "system-ui,sans-serif", color: "#64748b" }}>Memuat...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 28, fontFamily: "system-ui,sans-serif" }}>
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", padding: 14, borderRadius: 10, fontSize: 13 }}>
          {error}
          {error.toLowerCase().includes("failed to fetch") && (
            <div style={{ marginTop: 6, fontSize: 12 }}>
              Pastikan Edge Function <code>admin-users</code> sudah di-deploy dan tabel <code>profiles</code> sudah dibuat (lihat <code>supabase/schema_users.sql</code>).
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: 28, fontFamily: "system-ui,sans-serif" }}>
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", padding: 14, borderRadius: 10, fontSize: 13 }}>
          Akses ditolak — halaman ini hanya untuk akun dengan role <strong>admin</strong>.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 28, fontFamily: "system-ui,sans-serif", maxWidth: 960 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0 }}>Kelola User</h1>
        <button
          onClick={() => { setShowForm((v) => !v); setActionError(""); }}
          style={{ ...btnBase, background: showForm ? "#f1f5f9" : "#3b82f6", color: showForm ? "#475569" : "#fff" }}
        >
          {showForm ? "Batal" : "+ Tambah User"}
        </button>
      </div>

      {actionError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", padding: "10px 12px", borderRadius: 8, fontSize: 12, marginBottom: 14 }}>
          {actionError}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18, marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 5 }}>Email</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputStyle} placeholder="staff@email.com" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 5 }}>Nama (opsional)</label>
              <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} style={inputStyle} placeholder="Nama lengkap" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 5 }}>Password</label>
              <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} style={inputStyle} placeholder="Minimal 8 karakter" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 5 }}>Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} style={inputStyle}>
                <option value="admin">Admin</option>
                <option value="staff">Staff</option>
              </select>
            </div>
          </div>
          <button type="submit" disabled={actionBusy} style={{ ...btnBase, background: "#3b82f6", color: "#fff", opacity: actionBusy ? 0.6 : 1 }}>
            {actionBusy ? "Menyimpan..." : "Buat User"}
          </button>
        </form>
      )}

      <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
              <th style={th}>Email</th>
              <th style={th}>Role</th>
              <th style={th}>Dibuat</th>
              <th style={th}>Login Terakhir</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                <td style={td}>
                  <div style={{ fontWeight: 600, color: "#0f172a" }}>{u.email}</div>
                  {u.id === currentUser?.id && <div style={{ fontSize: 11, color: "#94a3b8" }}>(akun kamu)</div>}
                </td>
                <td style={td}>
                  <select
                    value={u.role}
                    disabled={actionBusy}
                    onChange={(e) => handleRoleChange(u.id, e.target.value)}
                    style={{ ...inputStyle, width: "auto", padding: "5px 8px", fontSize: 12 }}
                  >
                    <option value="admin">Admin</option>
                    <option value="staff">Staff</option>
                  </select>
                </td>
                <td style={td}>{fmtDate(u.created_at)}</td>
                <td style={td}>{fmtDate(u.last_sign_in_at)}</td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                  <button
                    onClick={() => { setResetTarget(u); setResetPassword(""); setActionError(""); }}
                    style={{ ...btnBase, background: "#fff", border: "1px solid #cbd5e1", color: "#475569", marginRight: 6 }}
                  >
                    Reset Password
                  </button>
                  <button
                    onClick={() => handleDelete(u)}
                    disabled={u.id === currentUser?.id || actionBusy}
                    style={{
                      ...btnBase, background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626",
                      opacity: u.id === currentUser?.id ? 0.4 : 1,
                      cursor: u.id === currentUser?.id ? "not-allowed" : "pointer",
                    }}
                  >
                    Hapus
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {resetTarget && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50,
        }}>
          <form onSubmit={handleResetSubmit} style={{ background: "#fff", borderRadius: 14, padding: 22, width: 340 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>Reset Password</div>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>{resetTarget.email}</div>
            <input
              type="password"
              required
              minLength={8}
              autoFocus
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              placeholder="Password baru (min. 8 karakter)"
              style={{ ...inputStyle, marginBottom: 14 }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" onClick={() => setResetTarget(null)} style={{ ...btnBase, flex: 1, background: "#f1f5f9", color: "#475569" }}>Batal</button>
              <button type="submit" disabled={actionBusy} style={{ ...btnBase, flex: 1, background: "#3b82f6", color: "#fff" }}>
                {actionBusy ? "..." : "Simpan"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const th = { padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase" };
const td = { padding: "10px 14px", verticalAlign: "middle" };
